'use client';

import { Button } from '@/shared/components/atoms/Button';
import { getCachedSession } from '@/features/auth/utils/sessionCache';
import { useMessage } from '@/shared/hooks/useMessage';
import { trackEvent } from '@/shared/utils/mixpanel';
import { cn } from '@/styles/utils';
import Image from 'next/image';
import { useRouter } from 'next/navigation';

interface SideBannerButtonProps {
  label: string;
  href: string;
  src: string;
  target?: string;
  variant?: 'button' | 'image';
  isLast?: boolean;
  requiresAuth?: boolean;
}

export function SideBannerButton({
  label,
  href,
  src,
  target,
  variant = 'button',
  isLast = false,
  requiresAuth = false,
}: SideBannerButtonProps) {
  const router = useRouter();
  const { showCustomMessage, showLoginRequired } = useMessage();

  const handleClick = async () => {
    trackEvent('Button Click', { 'Page Name': '홈 화면', 'Button Name': label });

    if (href === '/coming-soon') {
      showCustomMessage('더 나은 서비스를 위해 준비 중입니다.', '준비 중');
      return;
    }

    // 로그인이 필요한 경우 세션 체크
    if (requiresAuth) {
      const session = await getCachedSession();
      if (!session) {
        showLoginRequired(() => {
          router.push('/signin');
        });
        return;
      }
    }

    if (target === '_blank') {
      window.open(href, '_blank');
    } else {
      router.push(href);
    }
  };

  if (variant === 'image') {
    return (
      <Image
        src={src}
        onClick={handleClick}
        alt={label}
        width={108}
        height={232}
        className={cn(
          'cursor-pointer rounded-lg shadow-sm',
          !isLast && 'mb-2'
        )}
      />
    );
  }

  return (
    <Button
      onClick={handleClick}
      className={cn(
        'h-auto py-3 bg-[#F0F3F7] hover:bg-[#E5E9EF]',
        !isLast && 'mb-2'
      )}
    >
      <div className="flex flex-col items-center justify-center">
        <Image src={src} alt={label} width={40} height={40} className="mb-2" />
        <span className="text-(--secondary-700) body-16r">{label}</span>
      </div>
    </Button>
  );
}